// src/components/auth/AuthActionHandler.jsx
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const AuthActionHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loadUserProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('');
  const [actionCode, setActionCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const mode = searchParams.get('mode');
    const actionCode = searchParams.get('oobCode');
    
    if (!mode || !actionCode) {
      // No valid auth action, redirect to home
      navigate('/');
      return;
    }

    setMode(mode);
    setActionCode(actionCode);

    if (mode === 'resetPassword') {
      // Verify the password reset code and get email
      verifyPasswordResetCode(auth, actionCode)
        .then((email) => {
          setEmail(email);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Invalid password reset code:', error);
          toast.error('Invalid or expired password reset link');
          navigate('/');
        });
    } else {
      setLoading(false);
    }
  }, [searchParams, navigate]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      
      // Step 1: Reset the password
      await confirmPasswordReset(auth, actionCode, newPassword);
      toast.success('Password set successfully!');
      
      // Step 2: Automatically log the user in with their new password
      const userCredential = await signInWithEmailAndPassword(auth, email, newPassword);
      
      // Step 3: Load user profile to get role information
      const userProfile = await loadUserProfile(userCredential.user.uid);
      
      // Step 4: Direct redirect to admin dashboard for clinic admins
      if (userProfile?.role === 'admin') {
        toast.success('🎉 Password set successfully! Welcome to your clinic dashboard!');
        navigate('/admin');
      } else if (userProfile?.role === 'super_admin') {
        toast.success('Welcome back, Super Admin!');
        navigate('/super-admin');
      } else {
        toast.success('Password set successfully! Welcome to HealeHub!');
        navigate('/');
      }
      
    } catch (error) {
      console.error('Password reset or login error:', error);
      if (error.code === 'auth/weak-password') {
        toast.error('Password is too weak. Please choose a stronger password.');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Invalid email address.');
      } else {
        toast.error('Failed to complete setup. Please try again.');
      }
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Processing authentication action...</p>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'resetPassword') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-xl p-8 border border-gray-200">
            <div className="text-center mb-6">
              <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Set Your HealeHub Password</h2>
              <p className="text-gray-600">
                Create a secure password for your HealeHub admin account
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Account: <strong>{email}</strong>
              </p>
            </div>

            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your new password"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm your new password"
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Setting Password & Logging In...' : 'Set Password & Continue'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                After setting your password, you'll be automatically logged in to HealeHub
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle other auth actions (email verification, etc.)
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
          <p className="text-gray-600">Processing authentication action...</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Return to HealeHub
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthActionHandler;
